import styled, { css } from 'styled-components';

import { Box } from '@/components/Box';
import { Flex } from '@/components/Flex';
import { Grid } from '@/components/Grid';
import { ImagePlaceholder } from '@/components/ImagePlaceholder';
import { LegacyButton } from '@/components/LegacyButton';
import { NextImage } from '@/components/NextImage';
import { TaxedPrice } from '@/components/TaxedPrice';
import { Typography } from '@/components/Typography';

import { ReactComponent as DeleteSVG } from '@/assets/icons/delete.svg';

export const Wrapper = styled(Box)<{
  $hasExtendWarranty?: boolean;
  $hasRelatedStrap?: boolean;
  $isRelatedProduct?: boolean;
  $productType: string;
}>`
  display: flex;
  ${({
    $hasExtendWarranty,
    $hasRelatedStrap,
    $isRelatedProduct,
    $productType,
    theme,
  }) => css`
    ${($hasRelatedStrap || $hasExtendWarranty) &&
    css`
      padding-bottom: 0;
    `}
    ${$productType === 'watch' &&
    !($hasRelatedStrap && $hasExtendWarranty) &&
    css`
      padding-bottom: ${theme.spacing(4)};
    `}

    ${$isRelatedProduct &&
    css`
      padding-top: ${theme.spacing(2)};
    `}

    margin-bottom: ${$hasRelatedStrap || !$isRelatedProduct
      ? 0
      : theme.spacing(1)};
    z-index: 1;

    ${theme.media.md.up} {
      display: flex;
      margin-bottom: ${($productType === 'watch' &&
        ($hasRelatedStrap || $hasExtendWarranty)) ||
      ($isRelatedProduct &&
        $productType === 'strap' &&
        $hasRelatedStrap &&
        $hasExtendWarranty)
        ? 0
        : theme.spacing(2)};
    }

    ${(($productType === 'strap' && $hasRelatedStrap) ||
      ($productType === 'warranty' && $hasExtendWarranty)) &&
    css`
      border-top: 1px solid ${theme.colors.primaryAdd6};
    `}

    ${theme.media.md.up} {
      ${['strap', 'warranty', 'tool']?.includes($productType) &&
      $isRelatedProduct &&
      css`
        padding-left: 60px;
      `}
    }
  `}
`;

export const Card = styled(Grid)<{
  $hasCompatibleStraps?: boolean;
  $hasRelatedStrap?: boolean;
}>`
  display: flex;
  flex-direction: column;
  ${({ $hasCompatibleStraps, $hasRelatedStrap, theme }) => css`
    background-color: ${theme.colors.white};
    height: 100%;
    display: flex;
    ${theme.media.md.down} {
      grid-template-columns: 72px auto;
      grid-column-gap: ${theme.spacing(1)};
    }

    ${theme.media.md.up} {
      grid-template-rows: minmax(100px, 100%) 1fr;
      padding-left: ${theme.spacing(3)};
    }

    ${!$hasRelatedStrap &&
    $hasCompatibleStraps &&
    css`
      height: auto;
    `}
  `}
`;

/**
 * Container for product images with fixed dimensions to prevent layout shifts (CLS)
 * Mobile: 72x112px, Desktop: 88x132px
 */
export const PictureWrapper = styled(Box)<{ $isDesktop?: boolean }>`
  position: relative;
  flex-shrink: 0;

  ${({ $isDesktop }) => css`
    width: ${$isDesktop ? '88px' : '72px'};
    height: ${$isDesktop ? '132px' : '112px'};
    min-width: ${$isDesktop ? '88px' : '72px'};
    min-height: ${$isDesktop ? '132px' : '112px'};
  `}
`;

export const Picture = styled(NextImage)<{ $scaleRatio?: number }>`
  object-fit: contain;

  ${({ $scaleRatio }) => css`
    transform: scale(${$scaleRatio});
    /* Make the scaled image top aligned */
    object-position: center ${((($scaleRatio ?? 1) - 1) / 1.5) * 100}%;
  `}
`;

export const Placeholder = styled(ImagePlaceholder)<{ isDesktop?: boolean }>`
  ${({ isDesktop }) => css`
    ${isDesktop
      ? css`
          display: flex;
          height: 132px;
          width: 88px;
        `
      : css`
          height: 112px;
          width: 72px;
        `}
  `};
`;

export const MiddleWrapper = styled(Box)`
  display: flex;
  justify-content: space-between;
  align-items: start;
  width: 100%;

  a {
    text-decoration: none;
  }

  ${({ theme }) => css`
    padding-bottom: ${theme.spacing(2)};
  `}
`;

export const EndWrapper = styled(Flex)`
  flex-direction: column;
  align-items: flex-end;
  gap: ${({ theme }) => theme.spacing(2)};
  padding: 0;
`;

export const Name = styled(Typography)`
  text-transform: uppercase;

  ${({ theme }) => css`
    font-family: ${theme.fontFamilies.italianPlate};
    font-size: ${theme.fontSizes.p1};
    color: ${theme.colors.secondary};

    ${theme.media.md.up} {
      font-size: ${theme.fontSizes.h5};
    }
  `}
`;

export const SmallText = styled(Typography)`
  ${({ theme }) => css`
    font-size: ${theme.fontSizes.p2};
    color: ${theme.colors.secondaryGrey};
  `}
`;

export const DeleteMobile = styled.button`
  all: unset;
  cursor: pointer;
  width: 24px;
  height: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primaryBlue};
    outline-offset: 2px;
  }

  ${({ theme }) => css`
    ${theme.media.md.up} {
      display: none;
    }
  `}
`;

export const DeleteMobileIcon = styled(DeleteSVG)`
  width: 24px;
  height: 24px;
`;

export const DeleteDesktop = styled(LegacyButton)`
  &&& {
    cursor: pointer;
    display: none;
    height: fit-content;
    text-underline-offset: auto;

    ${({ theme }) => css`
      text-transform: capitalize;
      text-decoration-color: ${theme.colors.primaryBlue};
      ${theme.media.md.up} {
        display: block;
        font-weight: ${theme.fontWeights.regular};
        color: ${theme.colors.primaryBlue};
      }
    `}
  }
`;

export const Price = styled(TaxedPrice)`
  ${({ theme }) => css`
    & > p {
      font-family: ${theme.fontFamilies.italianPlate};
      font-size: ${theme.fontSizes.h5};
      color: ${theme.colors.primaryBlue};
    }
  `}
`;

export const Buttons = styled(Box)<{
  $isAddWarranty?: boolean;
}>`
  ${({ theme }) => css`
    /* the wrapper renders for every watch - collapse it when no action applies */
    &:empty {
      display: none;
    }

    margin-top: ${theme.spacing(2)};

    padding: 0 ${theme.spacing(2)};
    ${theme.media.md.up} {
      padding: 0;
    }

    & > button {
      border-top: 1px solid ${theme.colors.primaryAdd6};
    }

    ${theme.media.md.down} {
      margin-bottom: -${theme.spacing(2)};
      margin-inline: -${theme.spacing(2)};
    }

    ${theme.media.md.up} {
      padding-left: ${theme.spacing(0)};

      & > button {
        padding-left: 0;
      }
    }
  `}
`;

export const CardAndButtonsWrapper = styled(Box)`
  width: 100%;
`;

export const PreOrderNote = styled(Typography)`
  width: 100%;
  text-align: right;

  ${({ theme }) => css`
    font-size: ${theme.fontSizes.p3};
    padding-top: ${theme.spacing(1)};

    ${theme.media.md.down} {
      text-align: center;
    }
  `}
`;

export const BuckleWrapper = styled(Flex)`
  ${({ theme }) => css`
    padding: ${theme.spacing(1.5)};
    background-color: ${theme.colors.lightGrey};
  `}
`;

export const BuckleName = styled(Typography)`
  text-transform: uppercase;

  ${({ theme }) => css`
    color: ${theme.colors.secondary};
    font-size: ${theme.fontSizes.p2};
  `}
`;

export const BuckleImageWrapper = styled(Box)`
  position: relative;
  height: 48px;
  width: 48px;
`;

export const Image = styled(NextImage)<{ $scaleRatio?: number }>`
  object-fit: contain;

  ${({ $scaleRatio }) => css`
    transform: scale(${$scaleRatio});
  `}
`;

export const Notice = styled(Typography)`
  line-height: 1.25;

  ${({ theme }) => css`
    color: ${theme.colors.black};
    font-size: ${theme.fontSizes.p3};
    padding: ${theme.spacing(1)};
    background-color: ${theme.colors.primaryLightAdd2};
    margin-top: ${theme.spacing(1)};
    margin-bottom: ${theme.spacing(2)};
  `}
`;
